import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button } from 'primereact/button';
import { UserDetailsContext } from 'context/userDetailsContext';
import { DisburseFundModalV2 } from './components/DisburseFundModalV2/DisburseFundModalV2';
import { TransferFundModalV2 } from './components/TransferFundModalV2/TransferFundModalV2';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/trustAccountingHelpersV2';
import { handlePageNumberInPagination } from 'common/pagination';
import { getTrustAccountBalance } from 'services/billingServices';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TrustAccountingFiltersV2 from './components/TrustAccountingFiltersV2/TrustAccountingFiltersV2';
import {
  caseNameBody,
  dateBody,
  paidFromBody,
  paymentTypeBody,
  amountBody,
  paidToBody,
  referenceBody,
  rowClassName,
} from './helpers/trustAccountingDataTableHelpersV2';
import { useTrustAccountListV2Query } from '../hooks/queries';

const TrustAccountingV2 = ({ caseDetails, caseView, getCaseDetails, setActiveIndex, temporaryFundsTabIndex, isActiveTab = true }) => {
  const userContext = useContext(UserDetailsContext);
  const queryClient = useQueryClient();
  const [showDisburseFundModal, setShowDisburseFundModal] = useState(false);
  const [showTransferFundModal, setShowTransferFundModal] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pageLimit, setPageLimit] = useState(getPageLimit(false, caseView, userContext) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Map DataTable field names to API sort_by field names
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return ''; // Return empty string when no sort field selected
    const fieldMap = {
      record_created_date: 'record_created_date',
      time: 'time',
      amount: 'amount',
      case_name: 'case_name',
      contact_name: 'contact_name',
      entry_type: 'entry_type',
      payment_type: 'payment_type',
      paid_from: 'paid_from',
      paid_to: 'paid_to',
      related_to: 'related_to',
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // Return empty string when no sort order selected
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  // Extract date range from calendar
  const dateRange = useMemo(() => {
    return pickExtractedDate(dateFilter, userContext?.userDetails?.timezone);
  }, [dateFilter, userContext?.userDetails?.timezone]);

  // Normalize filter values for consistent query keys (empty strings -> null)
  const normalizedFilters = useMemo(() => {
    return {
      transaction_date_lower: dateRange.transaction_date_lower || null,
      transaction_date_upper: dateRange.transaction_date_upper || null,
    };
  }, [dateRange]);

  // React Query hook for fetching trust account transactions
  const { transactions, totalRecords, totalPages, isLoading, refetch } = useTrustAccountListV2Query({
    limit: pageLimit,
    page: currentPage,
    case_id: caseView ? caseDetails?.case_id : selectedAccount ? null : null,
    account_id: selectedAccount || (caseView && caseDetails?.default_trust_account?.[0]?.account_id) || null,
    account_type: null,
    transaction_date_lower: normalizedFilters.transaction_date_lower,
    transaction_date_upper: normalizedFilters.transaction_date_upper,
    sort_by: getCurrentSortField,
    sort_order: getCurrentSortOrder,
    enabled: isActiveTab,
  });

  // React Query for trust account balance
  const accountIdForBalance = selectedAccount || (caseView && caseDetails?.default_trust_account?.[0]?.account_id);
  const { data: balanceData } = useQuery({
    queryKey: ['trust-account-balance', accountIdForBalance, caseDetails?.case_id],
    queryFn: () => getTrustAccountBalance(accountIdForBalance, caseView ? caseDetails?.case_id : null),
    enabled: !!accountIdForBalance,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const trustBalance = balanceData?.data?.available_balance ? parseFloat(balanceData.data.available_balance) : 0;
  const openingBalance = balanceData?.data?.opening_balance ? parseFloat(balanceData.data.opening_balance) : 0;
  const reconciledBalance = balanceData?.data?.unmapped_qb_balance ? parseFloat(balanceData.data.unmapped_qb_balance) : 0;
  const totalTrustBalance = balanceData?.data?.total_trust_balance ? parseFloat(balanceData.data.total_trust_balance) : 0;
  const designatedAsTempTrustFunds = balanceData?.data?.designated_as_temp_trust_funds
    ? parseFloat(balanceData.data.designated_as_temp_trust_funds)
    : 0;
  const availableBalance = balanceData?.data?.available_balance ? parseFloat(balanceData.data.available_balance) : 0;

  useEffect(() => {
    if (caseView && caseDetails?.default_trust_account?.[0]?.account_id) {
      // Balance query will automatically fetch when accountIdForBalance is set
    }
  }, [caseView, caseDetails]);

  const onPageChange = (page) => {
    setCurrentPage(page);
    const path = getFilePathForPagination(false, caseView, userContext);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, page);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1);
    const path = getFilePathForPagination(false, caseView, userContext);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
    setCurrentPage(1);
  };

  const onChangeCalendar = (e) => {
    setDateFilter(e.value);
    setCurrentPage(1);
  };

  const onChangeAccount = (e) => {
    setSelectedAccount(e.value);
    setCurrentPage(1);
  };

  const refreshData = () => {
    setDateFilter(null);
    setSelectedAccount(null);
    setCurrentPage(1);
    // Don't call refetch() - React Query will automatically use cached data if available
    // The query will automatically re-run when state changes, and if the query key matches
    // a previous query with cached data (within staleTime), it will use the cache
  };

  const disburseFundCallback = () => {
    typeof getCaseDetails === 'function' && getCaseDetails();
    // Invalidate all trust account listV2 queries to ensure all pages/filters are updated
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return key[0] === 'trust-accounting' && key[1] === 'list' && key[2] === 'v2';
      },
    });
    // Invalidate balance query
    queryClient.invalidateQueries({ queryKey: ['trust-account-balance'] });
    refetch();
  };

  const transferFundCallback = () => {
    typeof getCaseDetails === 'function' && getCaseDetails();
    // Invalidate all trust account listV2 queries to ensure all pages/filters are updated
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return key[0] === 'trust-accounting' && key[1] === 'list' && key[2] === 'v2';
      },
    });
    // Invalidate balance query
    queryClient.invalidateQueries({ queryKey: ['trust-account-balance'] });
    refetch();
  };

  return (
    <>
      {caseView && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <Button
            label="Withdraw Funds"
            className="p-button p-button-primary me-2"
            onClick={() => setShowDisburseFundModal(true)}
            disabled={Number(trustBalance) === 0}
          />
          {caseDetails?.billing_preferences?.payment_structure === 'Flat Fee' && (
            <Button
              label="Transfer Funds"
              className="p-button p-button-primary"
              onClick={() => setShowTransferFundModal(true)}
              disabled={Number(trustBalance) <= 0}
            />
          )}
        </div>
      )}
      <TrustAccountingFiltersV2
        dateFilter={dateFilter}
        onChangeCalendar={onChangeCalendar}
        selectedAccount={selectedAccount}
        onChangeAccount={onChangeAccount}
        refreshData={refreshData}
        caseView={caseView}
        totalTrustBalance={totalTrustBalance}
        designatedAsTempTrustFunds={designatedAsTempTrustFunds}
        availableBalance={availableBalance}
        openingBalance={openingBalance}
        reconciledBalance={reconciledBalance}
      />

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={transactions || []}
          className="p-datatable-responsive"
          emptyMessage={isLoading ? 'Loading...' : 'No records found'}
          rowClassName={rowClassName}
          paginator={false}
          rows={pageLimit}
          storageKey="trust_accounting_column_widths"
          sortMode="server"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          columns={[
            {
              field: 'case_name',
              header: 'Case Name',
              body: (rowData) => caseNameBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              hidden: caseView,
              sortable: true,
            },
            {
              field: 'record_created_date',
              header: 'Date of Transaction',
              body: (rowData) => dateBody({ rowData, userContext }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'paid_from',
              header: 'Paid From',
              body: (rowData) => paidFromBody({ rowData, setActiveIndex, temporaryFundsTabIndex }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'payment_type',
              header: 'Payment Type',
              body: (rowData) => paymentTypeBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'amount',
              header: 'Amount',
              body: (rowData) => amountBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'paid_to',
              header: 'Paid To',
              body: (rowData) => paidToBody({ rowData, setActiveIndex, temporaryFundsTabIndex }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'related_to',
              header: 'Ref.',
              body: (rowData) => referenceBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
          ]}
        />

        <EllipsisPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          pageSize={pageLimit}
          totalRecords={totalRecords}
          onPageSizeChange={onPageSizeChange}
          showPageSizeDropdown={true}
        />
      </div>
      {showDisburseFundModal && (
        <DisburseFundModalV2 caseDetails={caseDetails} onHide={() => setShowDisburseFundModal(false)} callback={disburseFundCallback} />
      )}
      {showTransferFundModal && (
        <TransferFundModalV2
          caseDetails={caseDetails}
          onHide={() => setShowTransferFundModal(false)}
          callback={transferFundCallback}
          trustBalance={trustBalance}
        />
      )}
    </>
  );
};

export default TrustAccountingV2;
